import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Ticket, Building2, Users2, UsersRound, AlertTriangle, Inbox, CheckCircle2, Lock, Clock, Download, Flame, Calendar } from "lucide-react";
import { statusMeta, priorityMeta, formatDateShort, formatSeconds } from "@/lib/tickets";

function pctColor(pct) {
    if (pct == null) return "var(--text-muted)";
    if (pct >= 90) return "#16A34A";
    if (pct >= 70) return "#F59E0B";
    return "#DC2626";
}

export default function Dashboard() {
    const { user } = useAuth();
    const [stats, setStats] = useState({ users: null, companies: null, groups: null });
    const [ticketStats, setTicketStats] = useState(null);
    const [recent, setRecent] = useState([]);
    const [byCompany, setByCompany] = useState(null);
    const [myScope, setMyScope] = useState(null);
    const [criticalWeek, setCriticalWeek] = useState(null);
    const [exportingCsv, setExportingCsv] = useState(false);
    const [error, setError] = useState("");
    const isStaff = ["agent", "admin", "superadmin", "super_agent"].includes(user?.role);
    const isAdmin = ["admin", "superadmin"].includes(user?.role);
    const isSuperWatcher = user?.role === "super_agent" || user?.role === "super_customer";

    useEffect(() => {
        (async () => {
            try {
                const reqs = [
                    api.get("/companies"),
                    api.get("/groups"),
                    isAdmin ? api.get("/users") : Promise.resolve({ data: [] }),
                    api.get("/tickets/stats"),
                    api.get("/tickets", { params: { limit: 8 } }),
                ];
                const [c, g, u, ts, rt] = await Promise.all(reqs);
                setStats({
                    companies: c.data.length,
                    groups: g.data.length,
                    users: isAdmin ? u.data.length : null,
                });
                setTicketStats(ts.data);
                setRecent(rt.data);
                if (isStaff) {
                    try {
                        const bc = await api.get("/metrics/by-company");
                        setByCompany(bc.data);
                    } catch { /* ignore — non-blocking */ }
                    try {
                        const cw = await api.get("/tickets/critical-dates-this-week");
                        setCriticalWeek(cw.data);
                    } catch { /* ignore — non-blocking */ }
                }
                if (isSuperWatcher) {
                    try {
                        const ms = await api.get("/metrics/my-scope");
                        setMyScope(ms.data);
                    } catch { /* ignore */ }
                }
            } catch (err) {
                setError(formatApiError(err));
            }
        })();
    }, [isAdmin, isStaff, isSuperWatcher]);

    const exportCompanyCsv = async () => {
        setExportingCsv(true);
        try {
            const resp = await api.get("/metrics/by-company.csv", { responseType: "blob" });
            const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
            a.href = url;
            a.download = `desempeno_por_empresa_${stamp}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setExportingCsv(false);
        }
    };

    const [exportingMyScope, setExportingMyScope] = useState(false);
    const exportMyScopeCsv = async () => {
        setExportingMyScope(true);
        try {
            const resp = await api.get("/metrics/my-scope.csv", { responseType: "blob" });
            const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
            a.href = url;
            a.download = `mi_alcance_${user?.role || "scope"}_${stamp}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setExportingMyScope(false);
        }
    };

    return (
        <div data-testid="dashboard">
            <div className="page-head">
                <div>
                    <h1>Hola, {user?.name?.split(" ")[0] || "bienvenido"}</h1>
                    <p>{isStaff ? "Panel colaborativo de tickets y administración." : "Bienvenido a itotal4u — abre, sigue y resuelve tus solicitudes."}</p>
                </div>
                <Link to="/tickets/new" className="btn btn-primary" data-testid="dashboard-new-ticket"><Ticket size={16} /> Nuevo ticket</Link>
            </div>

            {error && <div className="alert alert-error">{error}</div>}

            {/* Ticket stats */}
            {ticketStats && (
                <div className="stats" data-testid="ticket-stats">
                    <div className="card stat" data-testid="stat-tickets-open">
                        <div className="flex items-center justify-between"><div className="k">Abiertos</div><Inbox size={16} color="#DC2626" /></div>
                        <div className="v">{(ticketStats.new || 0) + (ticketStats.open || 0)}</div>
                        <div className="d">nuevos + abiertos</div>
                    </div>
                    <div className="card stat" data-testid="stat-tickets-pending">
                        <div className="flex items-center justify-between"><div className="k">Pendientes</div><AlertTriangle size={16} color="#F59E0B" /></div>
                        <div className="v">{ticketStats.pending || 0}</div>
                        <div className="d">esperando cliente</div>
                    </div>
                    <div className="card stat" data-testid="stat-tickets-resolved">
                        <div className="flex items-center justify-between"><div className="k">Resueltos</div><CheckCircle2 size={16} color="#16A34A" /></div>
                        <div className="v">{ticketStats.resolved || 0}</div>
                        <div className="d">listos para cerrar</div>
                    </div>
                    <div className="card stat" data-testid="stat-tickets-total">
                        <div className="flex items-center justify-between"><div className="k">Total</div><Ticket size={16} color="var(--accent)" /></div>
                        <div className="v">{ticketStats.total || 0}</div>
                        <div className="d">
                            {isStaff && <>Tus asignaciones: <b style={{ color: "var(--text)" }}>{ticketStats.assigned_to_me ?? 0}</b> · Sin asignar: <b style={{ color: "var(--text)" }}>{ticketStats.unassigned ?? 0}</b></>}
                            {!isStaff && <>mis solicitudes</>}
                        </div>
                    </div>
                </div>
            )}

            {/* 🔥 Alerta de fechas críticas (vencidas o por vencer esta semana) — solo staff */}
            {isStaff && criticalWeek && criticalWeek.total > 0 && (
                <CriticalWeekAlert data={criticalWeek} />
            )}

            {/* Customer mini-dashboard (semáforo + métricas) */}
            {!isStaff && ticketStats && ticketStats.total > 0 && (
                <div className="card mb-6" data-testid="customer-dashboard" style={{ padding: 18 }}>
                    <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
                        <h3 style={{ margin: 0 }}>Mi soporte de un vistazo</h3>
                        <Link to="/tickets" className="linklike text-sm">Ver todos →</Link>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
                        <div data-testid="customer-stat-waiting-me" style={{ padding: 14, borderRadius: 10, background: "rgba(22,163,74,.08)", border: "1px solid rgba(22,163,74,.35)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                                <span style={{ width: 10, height: 10, borderRadius: 999, background: "#16A34A", boxShadow: "0 0 8px #16A34A99" }} />
                                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--text-muted)", fontWeight: 700 }}>Te toca a ti</div>
                            </div>
                            <div style={{ fontSize: 32, fontWeight: 700, color: "#16A34A", lineHeight: 1 }}>{ticketStats.waiting?.customer || 0}</div>
                            <div className="text-muted text-xs mt-2">Tickets que necesitan tu respuesta o aprobación.</div>
                        </div>
                        <div data-testid="customer-stat-waiting-agent" style={{ padding: 14, borderRadius: 10, background: "rgba(245,158,11,.08)", border: "1px solid rgba(245,158,11,.35)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                                <span style={{ width: 10, height: 10, borderRadius: 999, background: "#F59E0B", boxShadow: "0 0 8px #F59E0B99" }} />
                                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--text-muted)", fontWeight: 700 }}>En cancha del agente</div>
                            </div>
                            <div style={{ fontSize: 32, fontWeight: 700, color: "#F59E0B", lineHeight: 1 }}>{ticketStats.waiting?.agent || 0}</div>
                            <div className="text-muted text-xs mt-2">Estamos trabajando en estos.</div>
                        </div>
                        <div data-testid="customer-stat-waiting-third" style={{ padding: 14, borderRadius: 10, background: "rgba(220,38,38,.08)", border: "1px solid rgba(220,38,38,.35)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                                <span style={{ width: 10, height: 10, borderRadius: 999, background: "#DC2626", boxShadow: "0 0 8px #DC262699" }} />
                                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--text-muted)", fontWeight: 700 }}>Esperando a terceros</div>
                            </div>
                            <div style={{ fontSize: 32, fontWeight: 700, color: "#DC2626", lineHeight: 1 }}>{ticketStats.waiting?.third_party || 0}</div>
                            <div className="text-muted text-xs mt-2">En manos de un proveedor o área externa.</div>
                        </div>
                        <div data-testid="customer-stat-avg-fr" style={{ padding: 14, borderRadius: 10, background: "var(--bg-2)", border: "1px solid var(--border-strong)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                                <Clock size={12} color="var(--text-muted)" />
                                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--text-muted)", fontWeight: 700 }}>Primera respuesta</div>
                            </div>
                            <div style={{ fontSize: 24, fontWeight: 700, color: "var(--text)", lineHeight: 1.1 }}>
                                {ticketStats.avg_first_response_seconds != null
                                    ? formatSeconds(ticketStats.avg_first_response_seconds)
                                    : <span className="text-muted">—</span>}
                            </div>
                            <div className="text-muted text-xs mt-2">Promedio sobre {ticketStats.first_response_sample || 0} ticket{ticketStats.first_response_sample === 1 ? "" : "s"} contestado{ticketStats.first_response_sample === 1 ? "" : "s"}.</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Recent tickets */}
            <div className="card mb-6" data-testid="recent-tickets">
                <div className="card-header">
                    <h3>Tickets recientes</h3>
                    <Link to="/tickets" className="linklike text-sm">Ver todos →</Link>
                </div>
                <div className="table-wrap">
                    <table className="t">
                        <thead><tr><th>ID</th><th>Asunto</th><th>Estado</th><th>Prioridad</th>{isStaff && <th>Solicitante</th>}<th>Actualizado</th></tr></thead>
                        <tbody>
                            {recent.length === 0 && (<tr><td colSpan={isStaff ? 6 : 5} className="text-muted" style={{ textAlign: "center", padding: 40 }}>
                                Aún no hay tickets. <Link to="/tickets/new" className="linklike">Crea el primero</Link>.
                            </td></tr>)}
                            {recent.map((t) => {
                                const s = statusMeta(t.status), p = priorityMeta(t.priority);
                                return (
                                    <tr key={t.ticket_id} style={{ cursor: "pointer" }} onClick={() => { window.location.href = `/tickets/${t.ticket_id}`; }}>
                                        <td className="id-mono"><b>{t.ticket_id}</b></td>
                                        <td style={{ maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}><b>{t.subject}</b></td>
                                        <td><span className="badge" style={{ color: s.color, borderColor: s.color + "66", background: s.color + "18" }}>{s.label}</span></td>
                                        <td><span className="badge" style={{ color: p.color, borderColor: p.color + "66", background: p.color + "18" }}>{p.label}</span></td>
                                        {isStaff && <td className="text-muted text-sm">{t.requester_name}</td>}
                                        <td className="text-muted mono text-xs">{formatDateShort(t.updated_at)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* My scope (super_agent / super_customer) */}
            {isSuperWatcher && myScope && (
                <div className="card mb-6" data-testid="my-scope-card">
                    <div className="card-header">
                        <h3>Mi alcance — {myScope.role === "super_agent" ? "Super Agente" : "Super Cliente"}</h3>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <span className="text-muted text-sm">{(myScope.per_user || []).length} {myScope.watch_kind} · {(myScope.per_company || []).length} empresas vigiladas</span>
                            {((myScope.per_user || []).length > 0 || (myScope.per_company || []).length > 0) && (
                                <button
                                    className="btn btn-ghost btn-sm"
                                    onClick={exportMyScopeCsv}
                                    disabled={exportingMyScope}
                                    data-testid="my-scope-export-csv"
                                    title="Descargar mi alcance como CSV (Excel-friendly)"
                                >
                                    {exportingMyScope ? <span className="loader" /> : <><Download size={13} /> Exportar CSV</>}
                                </button>
                            )}
                        </div>
                    </div>
                    <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 18 }}>
                        {(myScope.per_user || []).length > 0 && (
                            <div>
                                <div className="text-muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 8 }}>
                                    {myScope.watch_kind === "agentes" ? "Por agente vigilado" : "Por cliente vigilado"}
                                </div>
                                <div className="table-wrap">
                                    <table className="t" data-testid="my-scope-users-table">
                                        <thead>
                                            <tr>
                                                <th>{myScope.watch_kind === "agentes" ? "Agente" : "Cliente"}</th>
                                                <th style={{ textAlign: "right" }}>Abiertos</th>
                                                <th style={{ textAlign: "right" }}>Resueltos</th>
                                                <th style={{ textAlign: "right" }}>Total</th>
                                                <th style={{ textAlign: "right" }}><Clock size={11} style={{ verticalAlign: "-1px" }} /> 1ª resp. avg</th>
                                                <th style={{ textAlign: "right" }}>SLA 1ª resp.</th>
                                                <th style={{ textAlign: "right" }}>SLA resol.</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {myScope.per_user.map((row) => {
                                                const filterParam = myScope.role === "super_agent" ? "assignee" : "requester";
                                                return (
                                                    <tr key={row.user_id} data-testid={`my-scope-user-row-${row.user_id}`}>
                                                        <td>
                                                            <Link to={`/tickets?${filterParam}=${row.user_id}`} className="linklike"><b>{row.name}</b></Link>
                                                            <div className="text-muted mono text-xs">{row.email}</div>
                                                        </td>
                                                        <td style={{ textAlign: "right" }}><b>{row.open_count}</b></td>
                                                        <td style={{ textAlign: "right" }} className="text-muted">{row.resolved_count}</td>
                                                        <td style={{ textAlign: "right" }} className="text-muted">{row.total_tickets}</td>
                                                        <td style={{ textAlign: "right" }} className="mono text-xs">
                                                            {row.avg_first_response_seconds != null ? formatSeconds(row.avg_first_response_seconds) : "—"}
                                                        </td>
                                                        <td style={{ textAlign: "right", color: pctColor(row.sla_first_response_ok_pct), fontWeight: 600 }}>
                                                            {row.sla_first_response_ok_pct != null ? `${row.sla_first_response_ok_pct}%` : "—"}
                                                        </td>
                                                        <td style={{ textAlign: "right", color: pctColor(row.sla_resolution_ok_pct), fontWeight: 600 }}>
                                                            {row.sla_resolution_ok_pct != null ? `${row.sla_resolution_ok_pct}%` : "—"}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                        {(myScope.per_company || []).length > 0 && (
                            <div>
                                <div className="text-muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 8 }}>Por empresa vigilada</div>
                                <div className="table-wrap">
                                    <table className="t" data-testid="my-scope-companies-table">
                                        <thead>
                                            <tr>
                                                <th>Empresa</th>
                                                <th style={{ textAlign: "right" }}>Abiertos</th>
                                                <th style={{ textAlign: "right" }}>Resueltos</th>
                                                <th style={{ textAlign: "right" }}>Total</th>
                                                <th style={{ textAlign: "right" }}><Clock size={11} style={{ verticalAlign: "-1px" }} /> 1ª resp. avg</th>
                                                <th style={{ textAlign: "right" }}>SLA 1ª resp.</th>
                                                <th style={{ textAlign: "right" }}>SLA resol.</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {myScope.per_company.map((row) => (
                                                <tr key={row.company_id} data-testid={`my-scope-company-row-${row.company_id}`}>
                                                    <td>
                                                        <Link to={`/tickets?company_id=${row.company_id}`} className="linklike"><b>{row.name}</b></Link>
                                                        {row.is_placeholder && <span className="badge" style={{ marginLeft: 8 }}>Placeholder</span>}
                                                        {row.is_confidential && (
                                                            <span className="badge" style={{ marginLeft: 8, background: "#991B1B", color: "white" }}>
                                                                <Lock size={10} style={{ verticalAlign: "-1px" }} /> Confidencial
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: "right" }}><b>{row.open_count}</b></td>
                                                    <td style={{ textAlign: "right" }} className="text-muted">{row.resolved_count}</td>
                                                    <td style={{ textAlign: "right" }} className="text-muted">{row.total_tickets}</td>
                                                    <td style={{ textAlign: "right" }} className="mono text-xs">
                                                        {row.avg_first_response_seconds != null ? formatSeconds(row.avg_first_response_seconds) : "—"}
                                                    </td>
                                                    <td style={{ textAlign: "right", color: pctColor(row.sla_first_response_ok_pct), fontWeight: 600 }}>
                                                        {row.sla_first_response_ok_pct != null ? `${row.sla_first_response_ok_pct}%` : "—"}
                                                    </td>
                                                    <td style={{ textAlign: "right", color: pctColor(row.sla_resolution_ok_pct), fontWeight: 600 }}>
                                                        {row.sla_resolution_ok_pct != null ? `${row.sla_resolution_ok_pct}%` : "—"}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                        {(myScope.per_user || []).length === 0 && (myScope.per_company || []).length === 0 && (
                            <div className="text-muted text-sm" style={{ textAlign: "center", padding: 24 }}>
                                Aún no tienes personas ni empresas vigiladas. Pide a un administrador que configure tu alcance.
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Per-company performance */}
            {isStaff && byCompany && byCompany.length > 0 && (
                <div className="card mb-6" data-testid="by-company-card">
                    <div className="card-header">
                        <h3>Desempeño por empresa</h3>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <span className="text-muted text-sm">Ordenado por tickets abiertos</span>
                            <button
                                className="btn btn-ghost btn-sm"
                                onClick={exportCompanyCsv}
                                disabled={exportingCsv}
                                data-testid="by-company-export-csv"
                                title="Descargar CSV listo para Excel"
                            >
                                {exportingCsv ? <span className="loader" /> : <><Download size={13} /> Exportar CSV</>}
                            </button>
                        </div>
                    </div>
                    <div className="table-wrap">
                        <table className="t" data-testid="by-company-table">
                            <thead>
                                <tr>
                                    <th>Empresa</th>
                                    <th style={{ textAlign: "right" }}>Abiertos</th>
                                    <th style={{ textAlign: "right" }}>Resueltos</th>
                                    <th style={{ textAlign: "right" }}>Total</th>
                                    <th style={{ textAlign: "right" }}><Clock size={11} style={{ verticalAlign: "-1px" }} /> 1ª resp. avg</th>
                                    <th style={{ textAlign: "right" }}>SLA 1ª resp.</th>
                                    <th style={{ textAlign: "right" }}>SLA resol.</th>
                                </tr>
                            </thead>
                            <tbody>
                                {byCompany.map((row) => (
                                    <tr key={row.company_id} data-testid={`by-company-row-${row.company_id}`}>
                                        <td>
                                            <Link to={`/tickets?company_id=${row.company_id}`} className="linklike">
                                                <b>{row.name}</b>
                                            </Link>
                                            {row.is_placeholder && <span className="badge" style={{ marginLeft: 8 }}>Placeholder</span>}
                                            {row.is_confidential && (
                                                <span className="badge" style={{ marginLeft: 8, background: "#991B1B", color: "white" }}>
                                                    <Lock size={10} style={{ verticalAlign: "-1px" }} /> Confidencial
                                                </span>
                                            )}
                                        </td>
                                        <td style={{ textAlign: "right" }}><b>{row.open_count}</b></td>
                                        <td style={{ textAlign: "right" }} className="text-muted">{row.resolved_count}</td>
                                        <td style={{ textAlign: "right" }} className="text-muted">{row.total_tickets}</td>
                                        <td style={{ textAlign: "right" }} className="mono text-xs">
                                            {row.avg_first_response_seconds != null ? formatSeconds(row.avg_first_response_seconds) : "—"}
                                        </td>
                                        <td style={{ textAlign: "right", color: pctColor(row.sla_first_response_ok_pct), fontWeight: 600 }}>
                                            {row.sla_first_response_ok_pct != null ? `${row.sla_first_response_ok_pct}%` : "—"}
                                        </td>
                                        <td style={{ textAlign: "right", color: pctColor(row.sla_resolution_ok_pct), fontWeight: 600 }}>
                                            {row.sla_resolution_ok_pct != null ? `${row.sla_resolution_ok_pct}%` : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Admin stats */}
            {isAdmin && (
                <div className="stats">
                    <div className="card stat" data-testid="stat-companies">
                        <div className="flex items-center justify-between"><div className="k">Empresas</div><Building2 size={16} color="var(--accent)" /></div>
                        <div className="v">{stats.companies ?? "—"}</div>
                        <div className="d">incluye placeholder "POR ASIGNAR"</div>
                    </div>
                    <div className="card stat" data-testid="stat-groups">
                        <div className="flex items-center justify-between"><div className="k">Grupos / Colas</div><UsersRound size={16} color="var(--accent)" /></div>
                        <div className="v">{stats.groups ?? "—"}</div>
                        <div className="d">colas activas</div>
                    </div>
                    <div className="card stat" data-testid="stat-users">
                        <div className="flex items-center justify-between"><div className="k">Usuarios totales</div><Users2 size={16} color="var(--accent)" /></div>
                        <div className="v">{stats.users ?? "—"}</div>
                        <div className="d">agentes, clientes, admins</div>
                    </div>
                </div>
            )}
        </div>
    );
}


// ---------------------------------------------------------------------------
// Alerta de fechas críticas vencidas o por vencer esta semana.
// Visible solo para staff en el Dashboard. Lista los primeros 5 tickets;
// los demás se acceden vía "Ver todos" que abre /reports con el filtro.
// ---------------------------------------------------------------------------
function CriticalWeekAlert({ data }) {
    const { items, total, overdue_total, upcoming_total, today, end_of_week } = data;
    const visible = items.slice(0, 5);
    const more = total - visible.length;

    // El borde y fondo cambian de intensidad si hay tickets ya vencidos.
    const hasOverdue = (overdue_total || 0) > 0;
    const accent = hasOverdue ? "#EF4444" : "#F59E0B";  // rojo vs ámbar

    const fmtRelative = (dateIso) => {
        if (!dateIso) return "—";
        const days = Math.round((new Date(dateIso).getTime() - new Date(today).getTime()) / 86400000);
        if (days < 0) return `vencida hace ${Math.abs(days)} día${Math.abs(days) === 1 ? "" : "s"}`;
        if (days === 0) return "vence HOY";
        if (days === 1) return "vence mañana";
        return `en ${days} días`;
    };

    return (
        <div
            className="card mb-6"
            data-testid="critical-week-alert"
            style={{
                padding: 18,
                background: `linear-gradient(135deg, ${accent}14, transparent)`,
                border: `1px solid ${accent}55`,
                borderLeft: `4px solid ${accent}`,
            }}
        >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: accent + "22", border: `1px solid ${accent}66`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {hasOverdue ? <Flame size={18} color={accent} /> : <Calendar size={18} color={accent} />}
                    </div>
                    <div>
                        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--text)" }}>
                            {hasOverdue ? "Fechas críticas vencidas o por vencer esta semana" : "Fechas críticas por vencer esta semana"}
                        </h3>
                        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 3 }}>
                            <b style={{ color: accent }}>{overdue_total || 0}</b> vencida{overdue_total === 1 ? "" : "s"} ·
                            <b style={{ color: "var(--text)", marginLeft: 4 }}>{upcoming_total || 0}</b> por vencer ·
                            <span className="text-muted" style={{ marginLeft: 4 }}>Hasta {end_of_week}</span>
                        </div>
                    </div>
                </div>
                <Link
                    to={`/reports?critical_dates_due_by=${end_of_week}`}
                    className="btn btn-sm"
                    data-testid="critical-week-view-all"
                    style={{ background: accent, color: "#fff", borderColor: accent }}
                >
                    Ver todos ({total})
                </Link>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {visible.map((it, i) => {
                    const isOver = (it.overdue_count || 0) > 0;
                    const itemAccent = isOver ? "#EF4444" : "#F59E0B";
                    return (
                        <Link
                            key={it.ticket_id}
                            to={`/tickets/${it.ticket_id}`}
                            data-testid={`critical-week-item-${i}`}
                            style={{
                                display: "grid",
                                gridTemplateColumns: "76px 1fr 1fr 130px",
                                gap: 12,
                                padding: "10px 12px",
                                background: "var(--bg-1)",
                                border: "1px solid var(--border)",
                                borderRadius: 8,
                                textDecoration: "none",
                                color: "var(--text)",
                                fontSize: 13,
                                alignItems: "center",
                                transition: "background .12s, border-color .12s",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = "var(--bg-2)"; e.currentTarget.style.borderColor = itemAccent + "66"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "var(--bg-1)"; e.currentTarget.style.borderColor = "var(--border)"; }}
                        >
                            <span className="mono text-xs" style={{ color: itemAccent, fontWeight: 700 }}>{it.ticket_id}</span>
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={it.subject}>
                                {it.subject}
                            </span>
                            <span className="text-muted" style={{ fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={it.nearest_event || ""}>
                                {it.nearest_event ? <>📌 {it.nearest_event}</> : <span className="text-dim">(sin evento)</span>}
                            </span>
                            <span style={{ fontSize: 11, textAlign: "right" }}>
                                <span className="text-muted">{it.nearest_date}</span>
                                <br />
                                <span style={{ color: itemAccent, fontWeight: 600 }}>{fmtRelative(it.nearest_date)}</span>
                            </span>
                        </Link>
                    );
                })}
                {more > 0 && (
                    <div className="text-muted text-sm" style={{ textAlign: "center", padding: 6 }}>
                        Y {more} más…
                    </div>
                )}
            </div>
        </div>
    );
}
